import type { AdminInitiativeLead } from '../api/admin';
import {
  findComponentProjectById,
  getAllConfiguredSubawardeeIds,
  getConfiguredSubawardeeIdsForComponentProject,
  COMPONENT_PROJECTS,
} from '../config/componentProjects';
import {
  findInitiativeLeadForSubawardee,
  subawardeeMatchesLeadPortfolio,
} from './initiativeLeadUtils';

export function isNonEmptySubawardeeId(id: string | null | undefined): id is string {
  return id != null && id.trim().length > 0;
}

export function collectSubawardeeIdsFromLeads(leads: AdminInitiativeLead[]): string[] {
  const ids = new Set<string>();
  for (const lead of leads) {
    for (const subawardee of lead.subawardees) {
      if (subawardee.trim()) ids.add(subawardee.trim());
    }
  }
  return Array.from(ids).sort((a, b) => a.localeCompare(b));
}

export function subawardeeBelongsToComponentProject(
  subawardeeId: string,
  componentProjectId: string | null | undefined,
  leads: AdminInitiativeLead[]
): boolean {
  const project = findComponentProjectById(componentProjectId);
  if (!project) return true;

  const configuredIds = getConfiguredSubawardeeIdsForComponentProject(componentProjectId);
  if (configuredIds.length > 0) {
    return subawardeeMatchesLeadPortfolio(subawardeeId, configuredIds);
  }

  const owner = findInitiativeLeadForSubawardee(subawardeeId, leads);
  if (!owner) return false;

  return owner.component_projects.some(
    (name) =>
      name.toLowerCase() === project.name.toLowerCase() ||
      name.toLowerCase() === project.id.toLowerCase()
  );
}

export function findComponentProjectIdForSubawardee(
  subawardeeId: string,
  leads: AdminInitiativeLead[]
): string | null {
  for (const project of COMPONENT_PROJECTS) {
    if (
      project.subawardees &&
      subawardeeMatchesLeadPortfolio(subawardeeId, [...project.subawardees])
    ) {
      return project.id;
    }
  }

  const owner = findInitiativeLeadForSubawardee(subawardeeId, leads);
  const projectName = owner?.component_projects[0];
  if (!projectName) return null;

  const project = findComponentProjectById(projectName);
  return project?.id ?? null;
}

export function filterSubawardeeIds(
  leads: AdminInitiativeLead[],
  options: {
    componentProjectId?: string | null;
    initiativeLeadId?: string | null;
  } = {}
): string[] {
  const configuredIds = getConfiguredSubawardeeIdsForComponentProject(options.componentProjectId);
  let ids: string[];

  if (configuredIds.length > 0) {
    ids = [...configuredIds].sort((a, b) => a.localeCompare(b));
  } else if (!options.componentProjectId) {
    // All projects: prefer the full config registry so the filter is complete even when
    // initiative-leads omit orgs; still include any extra ids returned by the API.
    const fromConfig = getAllConfiguredSubawardeeIds();
    const fromLeads = collectSubawardeeIdsFromLeads(leads);
    ids = Array.from(new Set([...fromConfig, ...fromLeads])).sort((a, b) => a.localeCompare(b));
  } else {
    ids = collectSubawardeeIdsFromLeads(leads);
  }

  if (options.componentProjectId && configuredIds.length === 0) {
    ids = ids.filter((id) =>
      subawardeeBelongsToComponentProject(id, options.componentProjectId, leads)
    );
  }

  if (options.initiativeLeadId) {
    const lead = leads.find((entry) => entry.id === options.initiativeLeadId);
    if (lead) {
      ids = ids.filter((id) => subawardeeMatchesLeadPortfolio(id, lead.subawardees));
    }
  }

  return ids;
}

export function buildSubawardeeLabelLookup(
  groups: { id: string; displayName: string }[]
): Record<string, string> {
  return Object.fromEntries(groups.map((group) => [group.id, group.displayName]));
}

export function getSubawardeeDisplayLabel(
  subawardeeId: string | null | undefined,
  labelById: Record<string, string>
): string | null {
  if (!isNonEmptySubawardeeId(subawardeeId)) return null;
  const trimmedId = subawardeeId.trim();
  const label = labelById[trimmedId] ?? labelById[subawardeeId];
  if (typeof label === 'string' && label.trim().length > 0) return label.trim();
  return trimmedId;
}
